import { ButtonDesigns, ButtonTypes } from '@paywithextend/components'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import { OrganizationRoles } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useFlyoutDispatch } from 'src/hooks'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import MyWalletAppRoute from 'src/navigation/appRoutes/myWallet.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  createBudgetUrl,
  createVirtualCardUrl,
  homeUrl,
  myWalletBudgetsPendingURL,
  myWalletBudgetsURL,
  myWalletPhysicalCardsUrl,
  myWalletVirtualCardsUrl,
  registerAccountUrl,
  requestBudgetUrl,
  requestVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  createSetIsCreateExpenseReportPopupOpen,
  createSetIsUploadReceiptsAutoMatchPopupOpen,
} from 'src/redux/actions/app.actions'
import { selectStatisticsDispatcherFromState } from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectMyWalletBudgetsMatch,
  selectMyWalletBudgetsPendingMatch,
  selectMyWalletPhysicalCardsMatch,
  selectMyWalletTransactionsMatch,
  selectMyWalletVirtualCardsMatch,
  selectSubmitExpensesRouteMatch,
} from 'src/redux/selectors/router.selectors'

const MyWalletWrapper: React.FC = () => {
  const history = useHistory()
  const { location: appLocation } = useExtendLocation()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const dispatch = useFlyoutDispatch()
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  const translate = useTranslate()

  const myWalletVirtualCardsMatch = useSelector(selectMyWalletVirtualCardsMatch)
  const myWalletPhysicalCardsMatch = useSelector(
    selectMyWalletPhysicalCardsMatch,
  )
  const myWalletTransactionsMatch = useSelector(selectMyWalletTransactionsMatch)
  const myWalletBudgetsMatch = useSelector(selectMyWalletBudgetsMatch)
  const myWalletBudgetsPendingMatch = useSelector(
    selectMyWalletBudgetsPendingMatch,
  )
  const myWalletReimbursementsMatch = useSelector(
    selectSubmitExpensesRouteMatch,
  )

  const creditCardsSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsCanIssueVirtualCardsCountStatisticsFromState,
  )

  const canIssueBudget = creditCardsSourceBudgetsEnabledCanIssueBudgetsCount > 0

  const canCreateVirtualCards =
    creditCardsCanIssueVirtualCardsCount > 0 &&
    creditCardsActiveCanIssueVirtualCardsCount > 0

  const user = useSelector(selectUserListModelFromState)
  const isGuestRole = user?.organizationRole === OrganizationRoles.guest
  const canRequestVirtualCard = !isGuestRole

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsSourceBudgetsEnabledCountStatistics,
      appStatisticsKeys.CreditCardsDelegateCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsCanIssueVirtualCardsCountStatistics,
    ])
  }, [dispatchStatistics])

  const {
    REGISTER_ACCOUNT: canRegisterAccount,
    VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures,
  } =
    useQueryUserFeatures([
      UserFeature.REGISTER_ACCOUNT,
      UserFeature.VIEW_VIRTUAL_CARD_FEATURES,
    ]).data || {}

  // REDIRECT EFFECT
  // If the user does not have a particular flag, redirect them to the page they
  // are authorized to see
  // If they can't see the physical or virtual card pages, replace with home screen
  // These redirect effects only work for virtualcard and physicalcard pages
  useEffect((): void => {
    // hasVirtualCardFeatures = FALSE
    // canRegisterAccount = TRUE
    // redirect to Physical Cards URL
    if (
      typeof hasVirtualCardFeatures === 'boolean' &&
      !hasVirtualCardFeatures &&
      typeof canRegisterAccount === 'boolean' &&
      canRegisterAccount &&
      (appLocation === AppLocation.MyWalletVirtualCards ||
        appLocation === AppLocation.MyWalletVirtualCardDetail)
    ) {
      history.replace(myWalletPhysicalCardsUrl())
      return
    }

    // hasVirtualCardFeatures = TRUE
    // canRegisterAccount = FALSE
    // redirect to Virtual Cards URL
    if (
      typeof canRegisterAccount === 'boolean' &&
      !canRegisterAccount &&
      typeof hasVirtualCardFeatures === 'boolean' &&
      hasVirtualCardFeatures &&
      appLocation === AppLocation.MyWalletPhysicalCards
    ) {
      history.replace(myWalletVirtualCardsUrl())
      return
    }

    // hasVirtualCardFeatures = FALSE
    // canRegisterAccount = FALSE
    // redirect to Home page
    if (
      typeof canRegisterAccount === 'boolean' &&
      !canRegisterAccount &&
      typeof hasVirtualCardFeatures === 'boolean' &&
      !hasVirtualCardFeatures &&
      (appLocation === AppLocation.MyWalletVirtualCards ||
        appLocation === AppLocation.MyWalletVirtualCardDetail ||
        appLocation === AppLocation.MyWalletPhysicalCards)
    ) {
      history.replace(homeUrl())
    }
  }, [hasVirtualCardFeatures, canRegisterAccount, history])

  const dispatchUploadReceiptsAutoMatchPopup = (): void => {
    dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(true))
  }

  const dispatchCreateExpenseReportPopup = (): void => {
    dispatch(createSetIsCreateExpenseReportPopupOpen(true))
  }

  let headerText: string
  if (myWalletVirtualCardsMatch || myWalletPhysicalCardsMatch) {
    headerText = 'Cards'
  } else if (myWalletTransactionsMatch) {
    headerText = 'Transactions'
  } else if (myWalletReimbursementsMatch) {
    headerText = 'Reimbursements'
  } else if (myWalletBudgetsMatch || myWalletBudgetsPendingMatch) {
    headerText = 'Budgets'
  } else {
    headerText = 'Not Found'
  }

  // const subtitleText = myWalletTransactionsMatch
  //     ? 'One place for all your card spend: review details, categorize expenses, and upload receipts'
  //     : undefined

  const myWalletPhysicalCardsButtons = [
    {
      design: ButtonDesigns.designCtaInverse,
      type: ButtonTypes.button,
      text: translate('Enroll Card'),
      linkProps: {
        to: registerAccountUrl(),
        ariaLabel: translate('Enroll Card'),
        testId: 'enroll-card-btn',
      },
    },
  ]

  const myWalletVirtualCardsButtons = [
    ...(canRequestVirtualCard
      ? [
          {
            design: ButtonDesigns.designCtaInverse,
            type: ButtonTypes.button,
            text: translate('Request Card'),
            linkProps: {
              to: requestVirtualCardUrl(),
              ariaLabel: translate('Request Card'),
            },
          },
        ]
      : []),
    ...(canCreateVirtualCards
      ? [
          {
            design: ButtonDesigns.designCta,
            type: ButtonTypes.button,
            text: translate('Create Card'),
            linkProps: {
              to: createVirtualCardUrl(),
              ariaLabel: translate('Create Card'),
            },
          },
        ]
      : []),
  ]

  const myWalletBudgetsButtons = [
    {
      design: ButtonDesigns.designCtaInverse,
      type: ButtonTypes.button,
      text: translate('Request Budget'),
      linkProps: {
        to: requestBudgetUrl(),
        ariaLabel: translate('Request Budget'),
      },
    },
    ...(canIssueBudget
      ? [
          {
            design: ButtonDesigns.designCta,
            type: ButtonTypes.button,
            text: translate('Create Budget'),
            linkProps: {
              to: createBudgetUrl(),
              ariaLabel: translate('Create Budget'),
            },
          },
        ]
      : []),
  ]

  const myWalletTransactionsButtons = [
    {
      design: ButtonDesigns.designCta,
      type: ButtonTypes.button,
      text: translate('Auto-Match Receipts'),
      onClick: dispatchUploadReceiptsAutoMatchPopup,
    },
  ]

  const myWalletReimbursementsButtons = [
    {
      design: ButtonDesigns.designCta,
      type: ButtonTypes.button,
      text: translate('Create Report'),
      onClick: dispatchCreateExpenseReportPopup,
    },
  ]

  // TODO: Restrict buttons by permissions / features

  /* @ts-ignore-next-line */
  let buttons = []
  if (myWalletPhysicalCardsMatch) {
    buttons = myWalletPhysicalCardsButtons
  } else if (myWalletVirtualCardsMatch) {
    buttons = myWalletVirtualCardsButtons
  } else if (myWalletBudgetsMatch || myWalletBudgetsPendingMatch) {
    buttons = myWalletBudgetsButtons
  } else if (myWalletTransactionsMatch) {
    buttons = myWalletTransactionsButtons
  } else if (myWalletReimbursementsMatch) {
    buttons = myWalletReimbursementsButtons
  } else {
    buttons = []
  }

  const subnavOptions = useMemo(() => {
    if (myWalletVirtualCardsMatch || myWalletPhysicalCardsMatch) {
      const baseOptions = []
      if (hasVirtualCardFeatures) {
        baseOptions.push({
          label: translate('Virtual Cards'),
          to: myWalletVirtualCardsUrl(),
          isActive: myWalletVirtualCardsMatch !== null,
          ariaLabelSource: translate('Virtual Cards'),
          'data-testid': 'MyWalletVirtualCards',
        })
      }

      return canRegisterAccount
        ? [
            {
              label: translate('Physical Cards'),
              to: myWalletPhysicalCardsUrl(),
              isActive: myWalletPhysicalCardsMatch !== null,
              ariaLabelSource: translate('Physical Cards'),
              'data-testid': 'MyWalletPhysicalCards',
            },
            ...baseOptions,
          ]
        : [...baseOptions]
    } else if (myWalletBudgetsMatch || myWalletBudgetsPendingMatch) {
      return [
        {
          label: translate('Overview'),
          to: myWalletBudgetsURL(),
          isActive: myWalletBudgetsMatch !== null,
          ariaLabelSource: translate('Overview'),
          'data-testid': 'MyWalletBudgets',
        },
        {
          label: translate('Pending Budgets'),
          to: myWalletBudgetsPendingURL(),
          isActive: myWalletBudgetsPendingMatch !== null,
          ariaLabelSource: translate('Pending Budgets'),
          'data-testid': 'MyWalletBudgetsPending',
        },
      ]
    } else {
      return []
    }
  }, [
    myWalletVirtualCardsMatch,
    myWalletPhysicalCardsMatch,
    myWalletTransactionsMatch,
    myWalletBudgetsMatch,
    myWalletBudgetsPendingMatch,
    myWalletReimbursementsMatch,
    canRegisterAccount,
    hasVirtualCardFeatures,
  ])

  return (
    <LayoutShell
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={subnavOptions}
      content={
        <Switch location={calculatedBackgroundLocation}>
          {MyWalletAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ))}
          <Route {...MyWalletAppRoute.routeProps} exact>
            <Rdrct to={myWalletVirtualCardsUrl()} />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
      title={<span>{translate(headerText)}</span>}
      // subtitle={(subtitleText && translate(subtitleText)) || undefined}
      /* @ts-ignore next-line */
      buttons={buttons}
    />
  )
}

export default MyWalletWrapper
